import React, { Dispatch, SetStateAction, useEffect } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  ImageCategory,
  ImageDimensionMap,
  ImageDimensions,
  OutfitRecoContent,
  RecoCardLayout,
  RecoCardProduct,
  RecoProductsData
} from 'types/outfitRecos';
import { constructMSAImageUrl, makeAscii } from 'helpers/index';
import MelodyCarousel from 'components/common/MelodyCarousel';
import { addImageDimensions, fetchProductStyleDetails } from 'actions/outfitRecos';
import { selectOutfitRecos } from 'selectors/outfitRecos';
import { ProductBundleOptions, ProductStyle } from 'types/cloudCatalog';
import CardPrice from 'components/common/card/CardPrice';
import { createCurrencyObj } from 'helpers/DataFormatUtils';
import ProductUtils from 'helpers/ProductUtils';
import { SmallLoader } from 'components/Loader';
import OutfitFeedback from 'components/outfitRecos/OutfitFeedback';
import { AppState } from 'types/app';
import Tag from 'styled/components/Tag/Tag';
import { heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import { track } from 'apis/amethyst';
import { evOutfitRecoCardClick } from 'events/outfitRecos';
import { generateOutfitGroup } from 'helpers/outfitRecoUtils';

import css from 'styles/components/outfitRecos/recosCardV3.scss';

interface RecosCardV3Props {
  contents: OutfitRecoContent[];
  contentIndex: number;
  setContentIndex: Dispatch<SetStateAction<number>>;
  setIsQuickViewModalOpen: Dispatch<SetStateAction<boolean>>;
  setInitialStyleId: Dispatch<SetStateAction<string | undefined>>;
}

interface ProductCardProps {
  openModal: () => void;
  setInitialStyleId: Dispatch<SetStateAction<string | undefined>>;
  recoProductsData: RecoProductsData;
  setIndexForModal: () => void;
  heartsData: any;
  triggerEventForRecoCardClick: (clickStyleId: string) => void;
}

export const RecosCardV3 = ({ contents, setIsQuickViewModalOpen, setInitialStyleId, setContentIndex }: RecosCardV3Props) => {
  const dispatch = useDispatch();

  const { recoProductsData, imageDimensions } = useSelector(selectOutfitRecos);
  const { hearts: heartsStyleIdsList = {}, products: heartsList, cookies } = useSelector(state => state);

  const { heartsStyleIds: hearts } = heartsStyleIdsList;
  const isCustomer = !!cookies['x-main'];

  useEffect(() => {
    contents.forEach(content => {
      const { productList } = content;
      productList.forEach(product => {
        const { imageId } = product;
        loadImage(imageId, dispatch);
        const fetchOpts: ProductBundleOptions = {
          styleId: product.styleId,
          entireProduct: false,
          includeBrand: false
        };
        dispatch(fetchProductStyleDetails(fetchOpts));
      });
    });
  }, [contents]);

  const openModal = () => setIsQuickViewModalOpen(true);

  const onHeartClick = ({ styleId, productId }: { styleId: string; productId: string }, isHearted: boolean) => {
    if (isCustomer) {
      const fn = isHearted ? unHeartProduct : heartProduct;
      // @ts-ignore
      dispatch(fn({ itemId: styleId, productId }));
    } else {
      dispatch(toggleHeartingLoginModal(true, styleId));
    }
  };

  const heartsData = {
    showFavoriteHeart: true,
    hearts,
    onHeartClick,
    heartsList,
    isDisplayCount: false
  };

  return (
    <MelodyCarousel isFullWidth={false} showDots={true} showArrows={true} arrowStyleOverrides={{ 'top': '350px', 'background-color': '#f5f5f5' }}>
      {contents.map((content, index) => {
        const { productList, layout, complementaryGroupId } = content;
        const setIndexForModal = () => setContentIndex(index);
        const triggerEventForRecoCardClick = (clickStyleId: string) => {
          const outfitGroup = generateOutfitGroup(complementaryGroupId, productList);
          const { styleId: seedStyleId } = productList.find(product => product.styleId)!;
          track(() => [
            evOutfitRecoCardClick,
            {
              seedStyleId,
              outfitGroup,
              clickStyleId,
              outfitNumber: index + 1
            }
          ]);
        };
        const productCardProps: ProductCardProps = {
          openModal,
          setInitialStyleId,
          recoProductsData,
          setIndexForModal,
          heartsData,
          triggerEventForRecoCardClick
        };
        return makeRecosCard(productList, layout, complementaryGroupId, imageDimensions, productCardProps);
      })}
    </MelodyCarousel>
  );
};

export default RecosCardV3;

const makeRecosCard = (
  products: RecoCardProduct[],
  cardLayout: RecoCardLayout,
  groupId: string,
  imageDimensions: ImageDimensionMap,
  productCardProps: ProductCardProps
) => {
  switch (cardLayout) {
    case RecoCardLayout.DEFAULT:
      if (products.length !== 4) return <></>;

      const [topLeftProduct, bottomLeftProduct, topRightProduct, bottomRightProduct] = products;

      const column1CSS = calculateProductSlotDimensions(topLeftProduct!, bottomLeftProduct!, imageDimensions);
      const column2CSS = calculateProductSlotDimensions(topRightProduct!, bottomRightProduct!, imageDimensions);

      return (
        <div key={groupId} className={css.recosCard}>
          <div className={css.defaultLayoutContainer}>
            <div className={css.column}>
              {makeProductSlot(`${groupId}-0`, topLeftProduct, column1CSS.topClass, productCardProps)}
              {makeProductSlot(`${groupId}-1`, bottomLeftProduct, column1CSS.bottomClass, productCardProps)}
            </div>
            <div className={css.column}>
              {makeProductSlot(`${groupId}-2`, topRightProduct, column2CSS.topClass, productCardProps)}
              {makeProductSlot(`${groupId}-3`, bottomRightProduct, column2CSS.bottomClass, productCardProps)}
            </div>
          </div>
          <OutfitFeedback groupId={groupId} products={products} />
        </div>
      );
    default:
      return <></>;
  }
};

const calculateProductSlotDimensions = (topProduct: RecoCardProduct, bottomProduct: RecoCardProduct, imageDimensions: ImageDimensionMap) => {
  const topProductDimensions = imageDimensions[topProduct.imageId];
  const bottomProductDimensions = imageDimensions[bottomProduct.imageId];
  const topProductCategory: ImageCategory = categorizeImage(topProductDimensions);
  const bottomProductCategory: ImageCategory = categorizeImage(bottomProductDimensions);

  if (topProductCategory === bottomProductCategory) {
    return {
      topClass: css.equalProduct,
      bottomClass: css.equalProduct
    };
  } else if (topProductCategory === ImageCategory.PORTRAIT) {
    return {
      topClass: css.bigProduct,
      bottomClass: css.smallProduct
    };
  } else {
    return {
      topClass: css.smallProduct,
      bottomClass: css.bigProduct
    };
  }
};

const categorizeImage = (imageDimensions: ImageDimensions = { height: 0, width: 0 }) => {
  const { height, width } = imageDimensions;
  if (height! > width!) {
    return ImageCategory.PORTRAIT;
  } else {
    return ImageCategory.LANDSCAPE;
  }
};

const loadImage = (imageId: string, dispatch: ThunkDispatch<AppState, void, AnyAction>) => {
  const imageUrl = constructMSAImageUrl(imageId, { autoCrop: true });
  const img = new Image();
  img.src = imageUrl;

  img.onload = () => {
    dispatch(
      addImageDimensions({
        [imageId]: {
          height: img.height,
          width: img.width
        }
      })
    );
  };
};

const makeProductSlot = (key: string, product: RecoCardProduct | undefined, className: string, productCardProps: ProductCardProps) => {
  const { styleId, productId, currentlyViewing } = product || { styleId: '' };

  const { openModal, setInitialStyleId, recoProductsData, setIndexForModal, heartsData, triggerEventForRecoCardClick } = productCardProps;

  if (!(styleId in recoProductsData)) {
    return (
      <div key={key} className={css.loader}>
        <SmallLoader />
      </div>
    );
  }

  const { detail: productInfo } = recoProductsData[styleId]!;
  const { brandName, productName } = productInfo;

  const selectedStyle: ProductStyle | undefined = productInfo.styles.find(style => style.styleId === styleId);

  const { productUrl, originalPrice, price } = selectedStyle || { productUrl: '', originalPrice: 0, price: 0 };
  const { imageId } = product!;

  const msaOpts = {
    autoCrop: true
  };

  const productCardClicked = () => {
    setIndexForModal();
    setInitialStyleId(styleId);
    openModal();
    triggerEventForRecoCardClick(styleId);
  };

  // Removed temporarily due to change in mocks
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const heartsProps = {
    heartsData,
    styleId,
    productId
  };

  return (
    <button key={key} type="button" onClick={productCardClicked} className={cn(css.productCard, className)}>
      {currentlyViewing && (
        <div className={css.currentlyViewing}>
          <Tag size="small" variant="blue 400 fill">
            Currently Viewing
          </Tag>
        </div>
      )}
      <img className={cn(css.productImage, className)} src={constructMSAImageUrl(imageId, msaOpts)} alt={productName} />
      {/* Removed temporarily due to change in mocks <Hearts {...heartsProps} /> */}
      <dl className={css.details}>
        <dt>Brand Name</dt>
        <dd className={css.mainText}>{makeAscii(brandName)}</dd>
        <dt>Product Name</dt>
        <dd className={css.subText}>{makeAscii(productName)}</dd>
        {price !== 0 && (
          <CardPrice
            price={createCurrencyObj(price)}
            msrp={createCurrencyObj(originalPrice)}
            onSale={ProductUtils.isStyleOnSale({ price, originalPrice })}
            isAvailable={true}
            url={productUrl}
          />
        )}
      </dl>
    </button>
  );
};
